import { initializeApp } from 'firebase/app';
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
} from 'firebase/auth';
import axios from 'axios';
import { getDeviceOS } from '../utils/helpers';

// API Base URL
export const BASE_URL = 'https://api.touch2success.com';

// Authorization token
const AUTH_TOKEN = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhdWQiOjMxODk3MjE3MSwiaWF0IjoxNzQ0NjM5NDgxLCJuYmYiOjE3NDQ2Mzk0ODEsImV4cCI6MzkwNjk0NDMwODEsInN1YiI6IiIsInNjb3BlcyI6WyIqIl19.5NQinsKdZsp9QEri60yDI-GqAn93_ZqpxNUr5McNr6E';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase (only if not already initialized)
let app;
try {
  app = initializeApp(firebaseConfig, 'auth-app');
} catch (error) {
  if (error.code === 'app/duplicate-app') {
    app = initializeApp(firebaseConfig);
  } else {
    throw error;
  }
}

const auth = getAuth(app);

/**
 * Set up reCAPTCHA verifier for phone authentication
 * @param {string} containerId - ID of the container element for reCAPTCHA
 * @returns {RecaptchaVerifier}
 */
export const setupRecaptcha = (containerId = 'recaptcha-container') => {
  try {
    const recaptchaVerifier = new RecaptchaVerifier(
      containerId,
      {
        size: 'invisible',
        callback: (response) => {
          console.log('reCAPTCHA solved');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
        },
      },
      auth
    );
    return recaptchaVerifier;
  } catch (error) {
    console.error('Error setting up reCAPTCHA:', error);
    throw error;
  }
};

/**
 * Send OTP to phone number
 * @param {string} phoneNumber - Phone number with country code (e.g., +1234567890)
 * @param {RecaptchaVerifier} recaptchaVerifier - reCAPTCHA verifier instance
 * @returns {Promise<ConfirmationResult>}
 */
export const sendOTP = async (phoneNumber, recaptchaVerifier) => {
  try {
    // Ensure phone number has country code
    const formattedPhone = phoneNumber.startsWith('+') 
      ? phoneNumber 
      : `+1${phoneNumber}`;

    const confirmationResult = await signInWithPhoneNumber(
      auth,
      formattedPhone,
      recaptchaVerifier
    );

    console.log('OTP sent successfully');
    return confirmationResult;
  } catch (error) {
    console.error('Error sending OTP:', error);
    throw error;
  }
};

/**
 * Verify OTP and sign in user
 * @param {ConfirmationResult} confirmationResult - Confirmation result from sendOTP
 * @param {string} otpCode - 6-digit OTP code
 * @returns {Promise<UserCredential>}
 */
export const verifyOTP = async (confirmationResult, otpCode) => {
  try {
    const result = await confirmationResult.confirm(otpCode);
    console.log('User signed in successfully:', result.user);
    return result;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    throw error;
  }
};

/**
 * Sign out current user
 * @returns {Promise<void>}
 */
export const logoutUser = async () => {
  try {
    await signOut(auth);
    console.log('User signed out successfully');
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Get current authenticated user
 * @returns {User|null}
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Listen to authentication state changes
 * @param {function} callback - Callback function to handle auth state changes
 * @returns {function} Unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  return auth.onAuthStateChanged(callback);
};

/**
 * Send OTP to phone number via API
 * @param {string} phone - Phone number
 * @param {string} otpType - Type of OTP (e.g., 'login', 'verify')
 * @param {string} locale - Locale (default: 'en')
 * @param {string} language - Language (default: 'en')
 * @returns {Promise<Object>} API response
 */
export const sendOTPAPI = async (phone, otpType = 'login', locale = 'en', language = 'en') => {
  try {
    const response = await axios.post(
      `${BASE_URL}/auth/otp`,
      {
        phone: phone,
        deviceOS: 'iOS',
        app_name: 'DIGI_SOLUTIONS',
        type: otpType,
      },
      {
        headers: {
          locale: locale,
          language: language,
          Authorization: `Bearer ${AUTH_TOKEN}`,
        },
      }
    );

    console.log('Login API result:', response.data);
    
    if (response.data?.message === 'success') {
      return { success: true, data: response.data };
    } else {
      return { success: false, data: response.data };
    }
  } catch (error) {
    console.error('API error', error);
    
    // Extract error message from error object
    let errorMessage = error.message || 'An error occurred';
    
    if (error.response?.data) {
      const errorData = error.response.data;
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = typeof errorData.error === 'string' 
          ? errorData.error 
          : errorData.error?.message || errorMessage;
      }
    }
    
    return { 
      success: false, 
      error: errorMessage,
      data: error.response?.data 
    };
  }
};

/**
 * Verify OTP via API
 * @param {string} phone - Phone number
 * @param {string} otp - OTP code
 * @param {string} locale - Locale (default: 'en')
 * @param {string} language - Language (default: 'en')
 * @returns {Promise<Object>} API response
 */
export const verifyOTPAPI = async (phone, otp, locale = 'en', language = 'en') => {
  try {
    const response = await axios.post(
      `${BASE_URL}/auth/otp/verify`,
      {
        phone: phone,
        deviceOS: 'iOS',
        app_name: 'DIGI_SOLUTIONS',
        otp: otp,
      },
      {
        headers: {
          locale: locale,
          language: language,
        },
      }
    );

    console.log('OTP Verify API result:', response.data);

    // Check if response has valid data array
    if (response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
      return { success: true, data: response.data.data };
    } else {
      return { success: false, data: response.data };
    }
  } catch (error) {
    console.error('OTP Verify API error:', error);
    
    // Extract error message from error object
    let errorMessage = error.message || 'An error occurred';
    
    if (error.response?.data) {
      const errorData = error.response.data;
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = typeof errorData.error === 'string' 
          ? errorData.error 
          : errorData.error?.message || errorMessage;
      }
    }
    
    return { 
      success: false, 
      error: errorMessage,
      data: error.response?.data 
    };
  }
};

export default {
  setupRecaptcha,
  sendOTP,
  verifyOTP,
  logoutUser,
  getCurrentUser,
  onAuthStateChange,
  sendOTPAPI,
  verifyOTPAPI,
};


